import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { POSTS } from "../data/content";
import PageHeader from "../components/PageHeader";
import AuthorCard from "../components/AuthorCard";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [featured, ...rest] = POSTS;
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/articles`)
      .then(({ data }) => setArticles(data || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="[ Blog / Académie ]"
        titleLines={["Le terrain,", "en pensée."]}
        intro="Analyses, méthodes et études de cas sur le Lean Management, l'Industrie 4.0 et l'excellence opérationnelle."
      />

      {articles.length > 0 && (
        <section className="bg-paper pt-16 md:pt-24" data-testid="blog-real-articles">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <Reveal>
              <div className="border-t hairline pt-4 mb-10 flex items-center justify-between">
                <span className="label-mono text-gold">Articles publiés</span>
                <span className="label-mono text-ink/45">Par Majdi Zarougui</span>
              </div>
            </Reveal>
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((a) => (
                <StaggerItem key={a.slug}>
                  <Link to={`/blog/${a.slug}`} data-testid={`real-article-${a.slug}`}
                    className="group h-full flex flex-col rounded-sm border-2 border-gold/50 bg-white p-7 hover:border-gold hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.3)] transition-all duration-500">
                    {a.category && <span className="label-mono text-gold">{a.category}</span>}
                    <h3 className="font-display text-2xl font-semibold leading-tight mt-3 group-hover:text-gold transition-colors duration-300">{a.title}</h3>
                    <p className="text-ink/55 text-sm mt-3 flex-1 leading-relaxed line-clamp-3">{a.excerpt}</p>
                    <div className="flex items-center justify-between mt-6 pt-5 border-t hairline label-mono text-ink/45">
                      <span>{new Date(a.published_at).toLocaleDateString("fr-FR")} · {a.read_time} min</span>
                      <span className="inline-flex items-center gap-1.5 text-ink">Lire <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" /></span>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      <section className="bg-paper py-16 md:py-24" data-testid="blog-featured">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <article className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-testid="featured-post">
              <div className="relative overflow-hidden rounded-sm order-1 lg:order-2">
                <img src={featured.image} alt={featured.title} className="w-full h-[320px] md:h-[440px] object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out" />
              </div>
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 label-mono text-gold">
                  <span>{featured.cat}</span><span className="text-ink/30">/</span><span className="text-ink/40">À la une</span>
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-semibold mt-5 leading-[1.02] tracking-tight group-hover:text-gold transition-colors duration-300">{featured.title}</h2>
                <p className="text-ink/60 text-lg mt-5 max-w-xl">{featured.excerpt}</p>
                <div className="flex items-center gap-4 mt-8 label-mono text-ink/50">
                  <span>{featured.date}</span><span>·</span><span>{featured.read} de lecture</span>
                </div>
                <button className="group/btn inline-flex items-center gap-2 mt-8 label-mono text-ink link-underline">
                  Lire l'article <ArrowUpRight size={15} className="group-hover/btn:rotate-45 transition-transform" />
                </button>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper pb-28" data-testid="blog-grid">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="border-t hairline pt-4 mb-10">
            <span className="label-mono text-ink/50">Tous les articles</span>
          </div>
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p) => (
              <StaggerItem key={p.title}>
                <article className="group h-full flex flex-col rounded-sm overflow-hidden border hairline bg-white hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.3)] transition-shadow duration-500" data-testid={`post-${p.title}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1100ms] ease-out" />
                    <span className="absolute top-4 left-4 label-mono text-paper bg-ink-900/70 backdrop-blur px-3 py-1.5 rounded-full">{p.cat}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-7">
                    <h3 className="font-display text-2xl font-semibold leading-tight group-hover:text-gold transition-colors duration-300">{p.title}</h3>
                    <p className="text-ink/55 text-sm mt-3 flex-1 leading-relaxed">{p.excerpt}</p>
                    <div className="flex items-center gap-3 mt-6 pt-5 border-t hairline label-mono text-ink/45">
                      <span>{p.date}</span><span>·</span><span>{p.read}</span>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-paper pb-28" data-testid="blog-author">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <AuthorCard />
        </div>
      </section>
    </>
  );
}